import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { StatStrip } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/hooks/use-app-context";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { earningsSteps } from "@/features/get-paid/earnings-steps";
import { contributorHomeCopy, type ContributorHomeCopy } from "./contributor-home-copy";
import { useContributorHome } from "./use-contributor-home";
import { normalizeUserRole } from "@/lib/auth/roles";
import { contributorTasks } from "@/features/contributor-tasks/contributor-tasks";
import { greetingName, lastSevenDays, type DayActivity } from "./weekly-activity";

const fill = (text: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((out, [key, value]) => out.split(`{${key}}`).join(String(value)), text);

/**
 * The contributor's home: what they earned, the one next step to a payout,
 * the tasks one click away, and their own week. Visitors keep the public page.
 */
export function ContributorHome({ lang }: { lang: string }) {
  const { user } = useApp();
  const copy: ContributorHomeCopy = contributorHomeCopy[lang === "en" ? "en" : "fr"];
  const locale = lang === "en" ? "en-GB" : "fr-FR";
  const base = `/${lang}`;
  const { stats, situation, history } = useContributorHome(user?.id ? String(user.id) : undefined);
  const name = greetingName(user as { name?: string; username?: string } | null);
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const amount = (value: number) => `${number(value)}\u00a0F\u00a0CFA`;

  // Same task list as the header and the mobile app home grid.
  const allTasks = contributorTasks(lang, { collector: normalizeUserRole(user?.role) === "collector" });
  const tasks = allTasks.filter((task) => task.primary);
  const otherTasks = allTasks.filter((task) => !task.primary);

  const s = stats.data;
  const week = lastSevenDays(
    (history.data?.data ?? []).filter((item) => item.date).map((item) => ({ date: item.date as string, contributions: 1, validations: 0 })),
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {name ? fill(copy.hello, { name }) : copy.helloNoName}
        </h1>
        <p className="text-sm text-muted-foreground">{copy.subtitle}</p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <BalanceCard copy={copy} base={base} amount={amount} situation={situation.data} loading={situation.isLoading} />

        <section aria-labelledby="home-tasks" className="space-y-3">
          <h2 id="home-tasks" className="text-sm font-medium text-muted-foreground">
            {copy.tasksTitle}
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2 [&>li:last-child:nth-child(odd)]:sm:col-span-2">
            {tasks.map((task) => (
              <li key={task.key}>
                <Link
                  to={task.href}
                  className="group flex h-full items-center gap-3 rounded-lg border border-border/60 bg-surface p-4 transition-colors hover:border-brand/40 hover:bg-brand/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="grid size-10 shrink-0 place-items-center rounded-md bg-brand/10 text-brand">
                    <Icon name={task.icon} className="size-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground">{task.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">{task.hint}</span>
                  </span>
                  <Icon name="arrowRight" className="size-4 shrink-0 text-faint-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-brand" />
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs text-faint-foreground">{copy.otherTasks}</span>
            {otherTasks.map((task) => (
              <Link
                key={task.key}
                to={task.href}
                className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-brand/40 hover:text-foreground"
              >
                <Icon name={task.icon} className="size-3.5" />
                {task.label}
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section aria-labelledby="home-figures" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="home-figures" className="text-sm font-medium text-muted-foreground">
            {copy.figuresTitle}
          </h2>
          <Link to={`${base}/my-contributions`} className="text-xs font-medium text-brand underline-offset-4 hover:underline">
            {copy.seeAll}
          </Link>
        </div>
        {stats.isLoading ? (
          <Skeleton className="h-[74px] bg-muted/40" />
        ) : stats.isError ? (
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-surface px-4 py-3 text-sm text-muted-foreground">
            {copy.loadError}
            <Button size="sm" variant="outline" onClick={() => void stats.refetch()}>
              {copy.retry}
            </Button>
          </div>
        ) : (
          <StatStrip
            items={[
              // Contributions from the stats; validated audio from the payout
              // situation, the figure the payout threshold is counted on.
              { label: copy.recorded, value: number(s?.global?.total ?? 0) },
              { label: copy.accepted, value: number(situation.data?.acceptedAudioCount ?? 0), tone: (situation.data?.acceptedAudioCount ?? 0) > 0 ? "success" : "default" },
              { label: copy.transcriptions, value: number(s?.transcription?.submitted ?? 0) },
              { label: copy.reviews, value: number((s?.validation?.validatedAudio ?? 0) + (s?.validation?.rejectedAudio ?? 0)) },
            ]}
          />
        )}
      </section>

      {!history.isLoading && !history.isError ? <WeekActivity copy={copy} days={week} locale={locale} format={number} /> : null}
    </div>
  );
}

function BalanceCard({
  copy,
  base,
  amount,
  situation,
  loading,
}: {
  copy: ContributorHomeCopy;
  base: string;
  amount: (value: number) => string;
  situation?: {
    step1Reached: boolean;
    step1Remaining: number;
    step1Progress: number;
    hasValidatedTest: boolean;
    tests: Record<string, unknown>;
    earnedAmount: number;
    awaitingPayoutAmount: number;
    hasPendingPayoutRequest: boolean;
    ratePerContribution: number;
  };
  loading: boolean;
}) {
  if (loading) return <Skeleton className="min-h-[220px] rounded-lg bg-muted/40" />;

  const earned = situation?.earnedAmount ?? 0;
  const steps = earningsSteps({
    thresholdReached: situation?.step1Reached ?? false,
    testSubmitted: Object.keys(situation?.tests ?? {}).length > 0,
    testApproved: situation?.hasValidatedTest ?? false,
    availableAmount: earned,
    hasPendingRequest: situation?.hasPendingPayoutRequest ?? false,
  });
  const next = situation?.hasPendingPayoutRequest
    ? fill(copy.nextPending, { amount: amount(situation.awaitingPayoutAmount) })
    : steps.contribute === "current"
      ? fill(copy.nextContribute, { remaining: situation?.step1Remaining ?? 0 })
      : steps.testPhase === "todo"
        ? copy.nextTest
        : steps.testPhase === "review"
          ? copy.nextReview
          : steps.canRequestPayout
            ? copy.nextPayout
            : copy.nextEarn;

  return (
    <section aria-labelledby="home-balance" className="flex flex-col rounded-lg border border-border/60 bg-surface p-5">
      <h2 id="home-balance" className="text-sm font-medium text-muted-foreground">
        {copy.balanceTitle}
      </h2>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-foreground">{amount(earned)}</p>
      <p className="mt-1 text-xs text-faint-foreground">{fill(copy.balanceHint, { rate: situation?.ratePerContribution ?? 5 })}</p>

      <div className="mt-5 rounded-md bg-brand/[0.06] p-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand">{copy.nextStep}</p>
        <p className="mt-1 text-sm font-medium text-foreground">{next}</p>
        {steps.contribute === "current" ? (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-border/70" role="progressbar" aria-valuenow={situation?.step1Progress ?? 0} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, situation?.step1Progress ?? 0)}%` }} />
          </div>
        ) : null}
      </div>

      <Button asChild size="sm" variant={steps.canRequestPayout ? "default" : "outline"} className="mt-4 self-start lg:mt-auto">
        <Link to={`${base}/get-paid?section=overview`}>{copy.openEarnings}</Link>
      </Button>
    </section>
  );
}

function WeekActivity({ copy, days, locale, format }: { copy: ContributorHomeCopy; days: DayActivity[]; locale: string; format: (n: number) => string }) {
  const total = days.reduce((sum, day) => sum + day.contributions, 0);
  const max = Math.max(1, ...days.map((day) => day.contributions));
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const fullDay = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" });
  const toDate = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  return (
    <section aria-labelledby="home-week" className="rounded-lg border border-border/60 bg-surface p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="home-week" className="text-sm font-medium text-muted-foreground">
          {copy.weekTitle}
        </h2>
        <span className="text-sm font-semibold tabular-nums text-foreground">{fill(copy.weekTotal, { count: format(total) })}</span>
      </div>
      {total === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">{copy.weekEmpty}</p>
      ) : (
        <ol className="mt-4 grid h-36 grid-cols-7 items-end gap-2">
          {days.map((day) => {
            const label = fill(copy.dayValue, { count: format(day.contributions), day: fullDay.format(toDate(day.date)) });
            return (
              <li key={day.date} className="flex h-full flex-col items-center justify-end gap-1.5" title={label}>
                <span className="sr-only">{label}</span>
                <span className="text-[11px] tabular-nums text-muted-foreground" aria-hidden="true">
                  {day.contributions > 0 ? format(day.contributions) : ""}
                </span>
                <span
                  aria-hidden="true"
                  className={cn("w-full max-w-10 rounded-t-[4px]", day.contributions > 0 ? "bg-primary" : "bg-border/70")}
                  style={{ height: `${day.contributions > 0 ? Math.max(6, (day.contributions / max) * 100) : 4}%` }}
                />
                <span className="text-[11px] capitalize text-faint-foreground" aria-hidden="true">
                  {weekday.format(toDate(day.date))}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
